/** Monthly pay statement — the same rendering for a labeler looking at their
    own account and for an admin looking at anyone's (with void buttons). */
import { api, fmtDate, fmtDuration, fmtEuro, fmtHours, fmtMonth } from "../api";
import { h } from "../dom";
import type { User } from "../types";

export interface WorkEntry {
  id: number;
  seconds: number;
  source: "heartbeat" | "backfill" | "manual";
  tile: string;
  assignment_id: number | null;
  note: string;
  created_by: string | null;
}

export interface StatementDay {
  day: string;
  seconds: number;
  rate: number | null;
  entries: WorkEntry[];
}

export interface LedgerEntry {
  id: number;
  kind: "payment" | "adjustment";
  day: string;
  amount_cents: number;
  note: string;
  created_by: string;
  created_at: number;
}

export interface StatementMonth {
  month: string;
  seconds: number;
  unpriced_seconds: number;
  periods: { cents_per_hour: number; seconds: number; cents: number }[];
  earned_cents: number;
  adjustment_cents: number;
  payment_cents: number;
  balance_start: number;
  balance_end: number;
  days: StatementDay[];
  ledger: LedgerEntry[];
}

export interface PayRate {
  id: number;
  valid_from: string;
  cents_per_hour: number;
  note: string;
  created_by_name: string;
  created_at: number;
}

export interface Statement {
  user_id: number;
  username: string;
  months: StatementMonth[];
  rates: PayRate[];
  current_rate: number | null;
  totals: {
    seconds: number;
    unpriced_seconds: number;
    earned_cents: number;
    adjustment_cents: number;
    payment_cents: number;
    balance_cents: number;
  };
}

/** Admin-only actions; absent for the labeler's own view. */
export interface StatementActions {
  voidWork: (entry: WorkEntry, day: string) => void;
  voidLedger: (entry: LedgerEntry) => void;
}

export function fmtRate(centsPerHour: number | null): string {
  return centsPerHour === null ? "no rate" : `${fmtEuro(centsPerHour)}/h`;
}

function balanceLabel(cents: number, own: boolean): string {
  if (cents > 0) return own ? "still owed to you" : "still owed";
  if (cents < 0) return own ? "paid ahead to you" : "paid ahead";
  return "settled";
}

function describe(e: WorkEntry): string {
  if (e.source === "manual") return `${e.note}${e.created_by ? ` (entered by ${e.created_by})` : ""}`;
  if (e.source === "backfill") return `${e.tile} — carried over, time before daily logging`;
  return e.tile;
}

export function renderStatement(st: Statement, own: boolean, actions?: StatementActions): HTMLElement {
  const t = st.totals;
  const root = h("div", { className: "statement" });

  // ---- summary
  root.append(
    h("div", { className: "pay-summary" },
      h("div", { className: "pay-big" },
        h("div", { className: "muted" }, balanceLabel(t.balance_cents, own)),
        h("div", { className: "pay-amount" + (t.balance_cents < 0 ? " pay-negative" : "") }, fmtEuro(Math.abs(t.balance_cents)))),
      h("div", { className: "pay-kpis" },
        kpi("Earned", fmtEuro(t.earned_cents + t.adjustment_cents), t.adjustment_cents ? `incl. ${fmtEuro(t.adjustment_cents)} adjustments` : ""),
        kpi("Paid", fmtEuro(t.payment_cents), ""),
        kpi("Time", fmtHours(t.seconds), fmtDuration(t.seconds)),
        kpi("Current rate", fmtRate(st.current_rate), st.rates.length > 1 ? `${st.rates.length} rate periods` : ""),
      )),
  );
  if (t.unpriced_seconds > 0) {
    root.append(h("div", { className: "banner" },
      `${fmtHours(t.unpriced_seconds)} of work fall on days without a rate and are not priced yet.`
      + (own ? " Ask your admin to set one." : " Add a rate starting early enough to cover them.")));
  }
  if (st.months.length === 0) {
    root.append(h("p", { className: "muted pad" }, "Nothing recorded yet."));
    return root;
  }

  // ---- months, newest first; the latest one starts expanded
  const months = [...st.months].reverse();
  months.forEach((m, i) => root.append(renderMonth(m, i === 0, own, actions)));
  return root;
}

function kpi(label: string, value: string, sub: string): HTMLElement {
  return h("div", { className: "pay-kpi" },
    h("div", { className: "muted" }, label),
    h("div", { className: "pay-kpi-value" }, value),
    sub ? h("div", { className: "muted small" }, sub) : null);
}

function renderMonth(m: StatementMonth, open: boolean, own: boolean, actions?: StatementActions): HTMLElement {
  const rates = m.periods.length
    ? m.periods.map((p) => fmtRate(p.cents_per_hour)).join(" · ")
    : m.seconds ? "no rate" : "–";
  const header = h("summary", { className: "pay-month-head" },
    h("span", { className: "pay-month-name" }, fmtMonth(m.month)),
    cell("Time", fmtHours(m.seconds)),
    cell("Rate", rates),
    cell("Earned", fmtEuro(m.earned_cents)),
    m.adjustment_cents ? cell("Adjustments", fmtEuro(m.adjustment_cents)) : null,
    cell("Paid", fmtEuro(m.payment_cents)),
    cell("Balance", fmtEuro(m.balance_end), m.balance_end < 0 ? "pay-negative" : ""),
  );

  const dayRows = m.days.map((d) =>
    h("tr", {},
      h("td", {}, d.day),
      h("td", { className: "num" }, fmtHours(d.seconds)),
      h("td", { className: "num" }, fmtRate(d.rate)),
      h("td", { className: "num" }, d.rate === null ? "–" : fmtEuro(Math.round(d.seconds * d.rate / 3600))),
      h("td", {}, h("ul", { className: "pay-entries" }, ...d.entries.map((e) =>
        h("li", { className: e.source === "manual" ? "pay-manual" : "" },
          `${fmtDuration(e.seconds)} — ${describe(e)}`,
          actions ? h("button", { className: "btn btn-small btn-link", title: "Void this entry (kept in the database, no longer counted)",
            onClick: () => actions.voidWork(e, d.day) }, "void") : null)))),
    ));
  const ledgerRows = m.ledger.map((l) =>
    h("tr", {},
      h("td", {}, l.day),
      h("td", {}, l.kind === "payment" ? "Payment" : "Adjustment"),
      h("td", { className: "num" + (l.kind === "payment" ? "" : l.amount_cents < 0 ? " pay-negative" : "") },
        l.kind === "payment" ? `− ${fmtEuro(l.amount_cents)}` : fmtEuro(l.amount_cents)),
      h("td", {}, l.note || h("span", { className: "muted" }, "–")),
      h("td", { className: "muted" }, `${l.created_by}, ${fmtDate(l.created_at)}`),
      actions ? h("td", {}, h("button", { className: "btn btn-small btn-link", onClick: () => actions.voidLedger(l) }, "void")) : null,
    ));

  const body = h("div", { className: "pay-month-body" },
    h("p", { className: "muted small" },
      `Balance carried in: ${fmtEuro(m.balance_start)} · earned ${fmtEuro(m.earned_cents)}`
      + (m.adjustment_cents ? ` · adjustments ${fmtEuro(m.adjustment_cents)}` : "")
      + ` · paid ${fmtEuro(m.payment_cents)} · carried out: ${fmtEuro(m.balance_end)}`
      + (m.days.length ? " · the month figure is exact, per-day amounts are rounded individually" : "")),
    m.days.length
      ? h("table", { className: "table pay-table" },
          h("thead", {}, h("tr", {}, ...["Day", "Time", "Rate", "Amount", "What"].map((x) => h("th", {}, x)))),
          h("tbody", {}, ...dayRows))
      : h("p", { className: "muted" }, "No work time this month."),
    ledgerRows.length
      ? h("table", { className: "table pay-table" },
          h("thead", {}, h("tr", {}, ...["Day", "", "Amount", "Note", "Entered by", actions ? "" : null].map((x) => x === null ? null : h("th", {}, x)))),
          h("tbody", {}, ...ledgerRows))
      : null,
  );
  return h("details", { className: "pay-month", open }, header, body);
}

function cell(label: string, value: string, cls = ""): HTMLElement {
  return h("span", { className: "pay-cell " + cls }, h("span", { className: "muted small" }, label + " "), value);
}

/** Labeler's own statement page (#/statement). */
export async function renderStatementPage(root: HTMLElement, user: User): Promise<void> {
  const content = h("div", { className: "page" }, h("p", { className: "muted pad" }, "Loading…"));
  root.append(
    h("div", { className: "topbar" },
      h("span", { className: "brand" }, "My statement"),
      h("span", { className: "spacer" }),
      h("span", { className: "muted" }, user.username),
      h("button", { className: "btn btn-small", onClick: () => (location.hash = "#/") }, "Back to tiles")),
    content,
  );
  const st = await api<Statement>("/api/me/statement");
  content.textContent = "";
  content.append(
    h("section", { className: "pad" },
      renderStatement(st, true),
      h("p", { className: "muted small" },
        "Time counts while you are actively working in the editor (calendar days in German time). "
        + "Payments are entered by the admins. Questions about an entry? Ask them — nothing here can be changed on your side.")),
  );
}
